import axios from "axios";
import apiConfig from "../config/api.js";

const API_URL = apiConfig.baseURL;

// Fonction helper pour obtenir le token
const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Service pour gérer les relations de suivi entre utilisateurs
 */

const followService = {
  /**
   * Suivre un utilisateur
   * @param {number} userId - ID de l'utilisateur à suivre
   * @returns {Promise} Résultat de la requête
   */
  followUser: async (userId) => {
    try {
      const response = await axios.post(
        `${API_URL}/follow`,
        { userId },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors du suivi:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Ne plus suivre un utilisateur
   * @param {number} userId - ID de l'utilisateur
   * @returns {Promise} Résultat de la requête
   */
  unfollowUser: async (userId) => {
    try {
      const response = await axios.delete(`${API_URL}/follow/${userId}`, {
        headers: getAuthHeader(),
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'arrêt du suivi:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les abonnés d'un utilisateur
   * @param {number} userId - ID de l'utilisateur
   * @param {number} limit - Limite de résultats
   * @returns {Promise} Liste des abonnés
   */
  getFollowers: async (userId, limit = 50) => {
    try {
      const response = await axios.get(
        `${API_URL}/follow/${userId}/followers`,
        {
          params: { limit },
          headers: getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des followers:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les abonnements d'un utilisateur
   * @param {number} userId - ID de l'utilisateur
   * @param {number} limit - Limite de résultats
   * @returns {Promise} Liste des abonnements
   */
  getFollowing: async (userId, limit = 50) => {
    try {
      const response = await axios.get(
        `${API_URL}/follow/${userId}/following`,
        {
          params: { limit },
          headers: getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des abonnements:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les utilisateurs suggérés
   * @param {number} limit - Limite de résultats
   * @returns {Promise} Liste des suggestions
   */
  getSuggestedUsers: async (limit = 10) => {
    try {
      const response = await axios.get(`${API_URL}/follow/suggestions/users`, {
        params: { limit },
        headers: getAuthHeader(),
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des suggestions:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Vérifier si on suit un utilisateur
   * @param {number} userId - ID de l'utilisateur
   * @returns {Promise<boolean>} True si on suit l'utilisateur
   */
  checkFollowStatus: async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/follow/${userId}/status`, {
        headers: getAuthHeader(),
      });
      return response.data.isFollowing;
    } catch (error) {
      console.error("Erreur lors de la vérification du statut:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Obtenir les statistiques de suivi d'un utilisateur
   * @param {number} userId - ID de l'utilisateur
   * @returns {Promise} Statistiques
   */
  getFollowStats: async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/follow/${userId}/stats`, {
        headers: getAuthHeader(),
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error);
      throw error.response?.data || error;
    }
  },
};

export default followService;
